import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, ArrowRight, Send } from "lucide-react";
import { company } from "../data/mock";

export default function ContactCTA() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    // mock: store locally
    try {
      const prev = JSON.parse(localStorage.getItem("rcw_enquiries") || "[]");
      prev.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem("rcw_enquiries", JSON.stringify(prev));
    } catch {}
    setSent(true);
    setForm({ name: "", email: "", phone: "", message: "" });
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-[#08080a] overflow-hidden">
      {/* Soft glow */}
      <div className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full bg-[#F5D000]/10 blur-[140px] pointer-events-none" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Left */}
          <div className="reveal">
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4">Get in Touch</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6">
              Got a project? <br />
              <span className="text-amber-accent">Let's build it.</span>
            </h2>
            <p className="text-white/65 max-w-md leading-relaxed mb-10">
              From £500 emergency repairs to £8M infrastructure programmes,
              tell us what you need and we'll get back within one working day.
            </p>

            <div className="space-y-5">
              <a href={`tel:${company.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-[#F5D000] group-hover:border-[#F5D000] transition-all">
                  <Phone className="w-5 h-5 text-white group-hover:text-black" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-white/45">Call</div>
                  <div className="font-display text-lg font-semibold text-white">{company.phone}</div>
                </div>
              </a>
              <a href={`mailto:${company.email}`} className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-[#F5D000] group-hover:border-[#F5D000] transition-all">
                  <Mail className="w-5 h-5 text-white group-hover:text-black" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-white/45">Email</div>
                  <div className="font-display text-lg font-semibold text-white">{company.email}</div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-white/45">Depot</div>
                  <div className="font-display text-lg font-semibold text-white">{company.location}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-white/45">Hours</div>
                  <div className="font-display text-lg font-semibold text-white">{company.hours}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right, Form */}
          <form
            onSubmit={submit}
            className="reveal relative p-8 lg:p-10 rounded-2xl bg-[#101014] border border-white/10"
          >
            <h3 className="font-display text-2xl font-bold mb-2">Request a quote</h3>
            <p className="text-sm text-white/55 mb-7">We typically respond within 24 hours.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2">Name</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-[#0a0a0c] border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F5D000] transition-colors"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2">Phone</label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-[#0a0a0c] border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F5D000] transition-colors"
                  placeholder="e.g. 07700 123456"
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2">Email</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-[#0a0a0c] border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F5D000] transition-colors"
                placeholder="you@company.com"
              />
            </div>
            <div className="mb-6">
              <label className="block text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2">Project details</label>
              <textarea
                required
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                className="w-full bg-[#0a0a0c] border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F5D000] transition-colors resize-none"
                placeholder="Tell us about the scope, location and timeline..."
              />
            </div>

            <button type="submit" className="btn-amber w-full px-6 py-4 rounded-md font-semibold text-sm flex items-center justify-center gap-2">
              {sent ? "✓ Sent, we'll be in touch" : (
                <>
                  Send Enquiry <Send className="w-4 h-4" />
                </>
              )}
            </button>
            <p className="text-[11px] text-white/35 mt-4 text-center">
              By submitting you agree to our privacy policy.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
